import React, { useState, useEffect } from "react";

/*
===========================================================================
SepLegChecklistComplet.jsx

Objectif : Proposer un questionnaire (dans le style des "timelines")
qui aboutit à une liste intelligente et exhaustive de documents requis
pour la procédure de séparation de corps, selon les réponses de l'utilisateur.

- Même logique et design que la DivorceChecklistComplet.
- On génère un "checklist" final dynamique, basé sur la logique
  des questions/réponses.
- Si certains documents (mariage/naissance) sont dans une langue
  autre que le français ou l'anglais, il faut une traduction certifiée
  faite au Québec.
- S'ils sont introuvables, possibilité d'une déclaration sous serment,
  mais consultez un avocat.

===========================================================================
*/

/*
-------------------------------------------------------------------------
Liste centrale de tous les documents potentiels, sous forme d'objets
(la plupart sont similaires à la version "Divorce" mais adaptés au contexte 
de la séparation de corps).
-------------------------------------------------------------------------
*/
const ALL_DOCUMENTS = {
  // Actes et certificats
  CERTIFICAT_MARIAGE: {
    id: "CERTIFICAT_MARIAGE",
    category: "Documents administratifs",
    label: "Certificat / acte de mariage (original ou copie officielle)",
    description:
      "Obligatoire pour prouver le mariage. Si le document est dans une langue autre que français ou anglais, vous devrez fournir une traduction certifiée faite au Québec. S'il est impossible d'obtenir ce certificat, vous pouvez produire une déclaration sous serment, mais ce n'est pas automatique ; consultez un avocat.",
  },
  CERTIFICAT_NAISSANCE_ENFANTS: {
    id: "CERTIFICAT_NAISSANCE_ENFANTS",
    category: "Documents administratifs",
    label: "Certificat de naissance pour chaque enfant mineur",
    description:
      "Requis pour prouver l'identité et la filiation des enfants. Si le document est dans une langue autre que français ou anglais, vous devrez fournir une traduction certifiée faite au Québec. S'il est introuvable, une déclaration sous serment peut être envisagée, mais parlez-en à un avocat.",
  },
  CERTIFICAT_NAISSANCE_EPOUX: {
    id: "CERTIFICAT_NAISSANCE_EPOUX",
    category: "Documents administratifs",
    label: "Certificat de naissance de l'époux(se)",
    description:
      "Parfois exigé pour des vérifications d'identité. Si le document est dans une langue autre que français ou anglais, une traduction certifiée est requise. S'il est introuvable, son remplacement par une déclaration sous serment doit être justifié devant le tribunal.",
  },

  // Traductions
  TRADUCTION_CERTIFIEE_MARIAGE: {
    id: "TRADUCTION_CERTIFIEE_MARIAGE",
    category: "Documents administratifs",
    label: "Traduction certifiée du certificat de mariage",
    description:
      "Vous avez indiqué que votre certificat de mariage est dans une autre langue que français ou anglais. Fournissez une traduction certifiée faite au Québec.",
  },
  TRADUCTION_CERTIFIEE_ENFANTS: {
    id: "TRADUCTION_CERTIFIEE_ENFANTS",
    category: "Documents administratifs",
    label: "Traduction certifiée des certificats de naissance des enfants",
    description:
      "Vous avez indiqué que les certificats de naissance des enfants sont dans une autre langue que français ou anglais. Fournissez des traductions certifiées faites au Québec.",
  },
  TRADUCTION_CERTIFIEE_EPOUX: {
    id: "TRADUCTION_CERTIFIEE_EPOUX",
    category: "Documents administratifs",
    label: "Traduction certifiée du certificat de naissance de l'époux(se)",
    description:
      "Vous avez indiqué que le certificat de naissance de l'époux(se) est dans une autre langue que français ou anglais. Fournissez une traduction certifiée faite au Québec.",
  },

  // Papiers financiers
  DECLARATION_IMPOTS_FEDERALE: {
    id: "DECLARATION_IMPOTS_FEDERALE",
    category: "Documents administratifs",
    label: "Déclaration d'impôts fédérale la plus récente",
    description:
      "Inclure l'avis de cotisation. (Typiquement l'année en cours ou la dernière année disponible.)",
  },
  DECLARATION_IMPOTS_PROVINCIALE: {
    id: "DECLARATION_IMPOTS_PROVINCIALE",
    category: "Documents administratifs",
    label: "Déclaration d'impôts provinciale la plus récente",
    description:
      "Inclure l'avis de cotisation. (Typiquement l'année en cours ou la dernière année disponible.)",
  },
  AVIS_COTISATION: {
    id: "AVIS_COTISATION",
    category: "Documents administratifs",
    label: "Avis de cotisation (fédéral et provincial)",
    description:
      "Documents émis par l'Agence du revenu du Canada et Revenu Québec confirmant le calcul définitif.",
  },
  RELEVES_PAIE: {
    id: "RELEVES_PAIE",
    category: "Documents administratifs",
    label: "Relevés de paie récents",
    description:
      "Démontre le revenu courant. Habituellement, on fournit les talons de paie des derniers mois.",
  },
  RELEVE_T4_ET_ETATS_FINANCIERS: {
    id: "RELEVE_T4_ET_ETATS_FINANCIERS",
    category: "Documents administratifs",
    label:
      "Relevé T4 (ou T2125) et états financiers (si travail autonome ou compagnie)",
    description:
      "Pour les entrepreneurs, inclut les déclarations de revenus d'entreprise et relevés nécessaires.",
  },

  // Formulaires obligatoires (adaptés pour séparation de corps)
  DEMANDE_INTRODUCTIVE_SEPARATION: {
    id: "DEMANDE_INTRODUCTIVE_SEPARATION",
    category: "Documents juridiques",
    label: "Demande introductive d'instance en séparation de corps",
    description:
      "Document officiel pour initier la procédure de séparation de corps devant la Cour supérieure.",
  },
  DEMANDE_MESURES_PROVISOIRES: {
    id: "DEMANDE_MESURES_PROVISOIRES",
    category: "Documents juridiques",
    label: "Demande de mesures provisoires",
    description:
      "Utile pour obtenir, à titre temporaire, une décision sur la garde, la pension, etc.",
  },
  ORDONNANCE_SAUVEGARDE: {
    id: "ORDONNANCE_SAUVEGARDE",
    category: "Documents juridiques",
    label: "Ordonnance de sauvegarde (si urgences/violence)",
    description:
      "Mesure rapide pour protéger la personne ou les enfants, en attente du jugement final.",
  },
  LISTE_PIECES: {
    id: "LISTE_PIECES",
    category: "Documents juridiques",
    label: "Liste de pièces",
    description:
      "Tableau ou liste des pièces justificatives déposées avec votre demande ou requête.",
  },
  ANNEXE_I_PENSION_ENFANTS: {
    id: "ANNEXE_I_PENSION_ENFANTS",
    category: "Documents juridiques",
    label: "Formulaire de fixation de la pension alimentaire pour enfants (Annexe I)",
    description:
      "Chiffrier officiel québécois pour calculer la pension selon les revenus et le temps de garde.",
  },
  FORMULAIRE_PENSION_EPOUX_FORM_III: {
    id: "FORMULAIRE_PENSION_EPOUX_FORM_III",
    category: "Documents juridiques",
    label: "Formulaire de fixation de la pension alimentaire pour ex-époux (Form. III)",
    description:
      "À remplir si vous réclamez une pension alimentaire pour l'ex-époux. Voir avec votre avocat.",
  },
  CONTRAT_DE_MARIAGE: {
    id: "CONTRAT_DE_MARIAGE",
    category: "Documents juridiques",
    label: "Contrat de mariage (si applicable)",
    description:
      "Si vous aviez signé un contrat notarié, le fournir pour déterminer le régime matrimonial.",
  },
  FORMULAIRE_REPONSE: {
    id: "FORMULAIRE_REPONSE",
    category: "Documents juridiques",
    label: "Formulaire de réponse (si vous répondez à la demande)",
    description:
      "Pour contester ou répondre à une demande en séparation déposée par votre conjoint(e).",
  },
  PROTOCOLE_INSTANCE: {
    id: "PROTOCOLE_INSTANCE",
    category: "Documents juridiques",
    label: "Protocole d'instance",
    description:
      "Organise le déroulement du dossier (délais, gestion de preuves). Souvent requis en cas de contestation.",
  },
  FORMULAIRE_444: {
    id: "FORMULAIRE_444",
    category: "Documents juridiques",
    label: "Déclaration (article 444 C.p.c.)",
    description:
      "Obligatoire si une pension alimentaire (enfants ou ex-époux) est demandée.",
  },

  // Liste actifs / passifs
  LISTE_ACTIFS_PASSIFS: {
    id: "LISTE_ACTIFS_PASSIFS",
    category: "Documents juridiques",
    label: "Liste des actifs et passifs (dettes)",
    description:
      "Inventaire complet : biens, comptes bancaires, placements, hypothèques, prêts, cartes de crédit, etc.",
  },
};

/*
-------------------------------------------------------------------------
Définition des étapes du questionnaire, adaptées pour la séparation de corps.
-------------------------------------------------------------------------
*/
const stepsData = [
  {
    id: "etape_situationFamiliale",
    title: "Situation familiale",
    shortContent:
      "Indiquez votre situation conjugale et la disponibilité de certains documents d'état civil.",
    detailedContent: `
      Répondez aux questions pour déterminer si un acte de mariage 
      et/ou des certificats de naissance sont requis 
      et dans quelle langue ils se trouvent (français/anglais ou autre).
      <br />
      S'ils sont indisponibles, il est parfois possible de produire 
      une déclaration sous serment, mais ce n'est pas automatique : 
      vous devez en parler à un avocat.
    `,
    questions: [
      {
        id: "q_etat_mariage",
        label: "Avez-vous un certificat/acte de mariage officiel ?",
        options: [
          { value: "oui", label: "Oui, je l'ai en ma possession" },
          { value: "non", label: "Non, je ne l'ai pas encore ou il est introuvable" },
        ],
        help: "Sans cet acte, une déclaration sous serment peut parfois être présentée, mais ce n'est pas garanti.",
      },
      {
        id: "q_langue_mariage",
        label: "Si oui, dans quelle langue est votre certificat de mariage ?",
        options: [
          { value: "fr_en", label: "Français ou anglais" },
          { value: "autre", label: "Autre langue (besoin d'une traduction certifiée)" },
        ],
        showIf: (answers) =>
          answers["etape_situationFamiliale"]?.q_etat_mariage === "oui",
        help: "Une traduction certifiée faite au Québec est requise si le document n'est ni en français ni en anglais.",
      },
      {
        id: "q_enfants_mineurs",
        label: "Avez-vous des enfants mineurs ?",
        options: [
          { value: "oui", label: "Oui (un ou plusieurs)" },
          { value: "non", label: "Non, aucun enfant mineur" },
        ],
        help: "Si oui, vous devrez fournir leur(s) certificat(s) de naissance.",
      },
      {
        id: "q_langue_enfants",
        label: "Si oui, dans quelle langue sont les certificats de naissance de vos enfants ?",
        options: [
          { value: "fr_en", label: "Français ou anglais" },
          { value: "autre", label: "Autre langue (besoin d'une traduction certifiée)" },
        ],
        showIf: (answers) =>
          answers["etape_situationFamiliale"]?.q_enfants_mineurs === "oui",
        help: "Une traduction certifiée est requise si ces documents ne sont pas en français ou en anglais.",
      },
      {
        id: "q_certificat_naissance_epoux",
        label: "Possédez-vous le certificat de naissance (version intégrale) de l'époux(se) ?",
        options: [
          { value: "oui", label: "Oui" },
          { value: "non", label: "Non (indisponible/introuvable)" },
        ],
        help: "Ce document peut parfois être exigé pour vérification d'identité ou de date de naissance.",
      },
      {
        id: "q_langue_epoux",
        label: "Si oui, dans quelle langue est ce certificat de naissance ?",
        options: [
          { value: "fr_en", label: "Français ou anglais" },
          { value: "autre", label: "Autre langue (besoin d'une traduction certifiée)" },
        ],
        showIf: (answers) =>
          answers["etape_situationFamiliale"]?.q_certificat_naissance_epoux === "oui",
        help: "Une traduction certifiée est requise si le document n'est ni en français ni en anglais.",
      },
    ],
  },

  {
    id: "etape_finances",
    title: "Situation financière",
    shortContent: "Documents d'impôts, relevés de paie, etc.",
    detailedContent: `
      Indiquez si vous disposez de vos déclarations d'impôts (fédérale et provinciale), 
      avis de cotisation, relevés de paie ou autres documents financiers spécifiques. 
      Ceci servira à calculer la pension (enfants ou ex-époux) et à évaluer les revenus.
    `,
    questions: [
      {
        id: "q_declarations_impots",
        label: "Avez-vous vos déclarations d'impôts (fédérale et provinciale) les plus récentes ?",
        options: [
          { value: "oui", label: "Oui, avec avis de cotisation" },
          { value: "non", label: "Non, je dois encore les obtenir" },
        ],
        help: "Généralement, on fournit la dernière année d'imposition disponible.",
      },
      {
        id: "q_releves_paie",
        label: "Avez-vous vos relevés de paie récents (pour démontrer votre revenu courant) ?",
        options: [
          { value: "oui", label: "Oui, ils sont prêts" },
          { value: "non", label: "Non, je dois en faire la demande à mon employeur" },
        ],
      },
      {
        id: "q_entreprise",
        label: "Êtes-vous travailleur autonome ou propriétaire d'une compagnie ?",
        options: [
          { value: "oui", label: "Oui" },
          { value: "non", label: "Non" },
        ],
        help: "Si oui, vous devrez produire vos états financiers et relevés T4, T2125, etc.",
      },
    ],
  },

  {
    id: "etape_procedureSeparation",
    title: "Procédure de séparation de corps",
    shortContent: "Quels actes ou requêtes comptez-vous déposer / remplir ?",
    detailedContent: `
      Selon votre situation, vous pourriez avoir besoin de déposer 
      une demande introductive en séparation de corps, ou de répondre à la demande de votre conjoint(e). 
      Vous pourriez aussi avoir besoin de mesures provisoires ou d'une ordonnance de sauvegarde 
      (en cas d'urgence).
    `,
    questions: [
      {
        id: "q_demande_separation",
        label: "Allez-vous initier la demande de séparation de corps (demande introductive) ?",
        options: [
          { value: "oui", label: "Oui, je suis le demandeur (ou la demanderesse)" },
          { value: "non", label: "Non, je réponds à la demande de mon conjoint(e)" },
        ],
      },
      {
        id: "q_mesures_provisoires",
        label: "Souhaitez-vous demander des mesures provisoires ?",
        options: [
          { value: "oui", label: "Oui (garde, pension, etc.)" },
          { value: "non", label: "Non" },
        ],
        help: "Exemples : pension alimentaire temporaire, garde, occupation du domicile, etc.",
      },
      {
        id: "q_ordonnance_sauvegarde",
        label: "Avez-vous une situation urgente nécessitant une ordonnance de sauvegarde ?",
        options: [
          { value: "oui", label: "Oui (urgence, violence, etc.)" },
          { value: "non", label: "Non" },
        ],
      },
    ],
  },

  {
    id: "etape_pensionAlimentaire",
    title: "Pension alimentaire",
    shortContent: "Indiquez si vous demandez une pension pour enfants et/ou pour vous-même (ex-époux).",
    detailedContent: `
      Les documents à fournir varient selon que vous demandez une pension alimentaire 
      pour enfant(s), pour l'ex-époux, ou les deux. 
      Vous devrez éventuellement remplir des formulaires supplémentaires (Annexe I, Formulaire III).
    `,
    questions: [
      {
        id: "q_pension_enfants",
        label: "Demandez-vous une pension alimentaire pour enfant(s) ?",
        options: [
          { value: "oui", label: "Oui (si enfants mineurs)" },
          { value: "non", label: "Non" },
        ],
        help: "Si vous avez des enfants mineurs, la pension alimentaire pour enfant peut être obligatoire.",
      },
      {
        id: "q_pension_epoux",
        label: "Demandez-vous une pension alimentaire pour ex-époux ?",
        options: [
          { value: "oui", label: "Oui" },
          { value: "non", label: "Non" },
        ],
        help: "Certains cas justifient la demande d'une pension pour l'ex-époux.",
      },
      {
        id: "q_declaration_444",
        label: "Savez-vous si la Déclaration (article 444 C.p.c.) vous concerne ?",
        options: [
          { value: "oui", label: "Oui, je devrai la remplir (pension prévue)" },
          { value: "non", label: "Non / pas sûr" },
        ],
        help: "Dès qu'il y a une demande de pension alimentaire, la déclaration 444 est normalement requise.",
      },
    ],
  },

  {
    id: "etape_documentsAccessoires",
    title: "Documents accessoires et pièces justificatives",
    shortContent: "Contrat de mariage, liste d'actifs/passifs, etc.",
    detailedContent: `
      Si vous avez un contrat de mariage ou si vous devez dresser 
      la liste de vos actifs et passifs (pour le partage des biens), 
      indiquez-le ci-dessous.
    `,
    questions: [
      {
        id: "q_contrat_mariage",
        label: "Aviez-vous signé un contrat de mariage (notarié) ?",
        options: [
          { value: "oui", label: "Oui" },
          { value: "non", label: "Non" },
        ],
      },
      {
        id: "q_liste_actifs_passifs",
        label: "Allez-vous produire la liste de vos actifs et passifs (dettes) ?",
        options: [
          { value: "oui", label: "Oui, nécessaire pour le partage du patrimoine familial" },
          { value: "non", label: "Non, pas pour l'instant" },
        ],
      },
    ],
  },
];

/*
-------------------------------------------------------------------------
Composant principal : SepLegChecklistComplet
-------------------------------------------------------------------------
*/
export default function SeparationChecklist() {
  const [completedSteps, setCompletedSteps] = useState([]);
  const [openStep, setOpenStep] = useState(null);
  const [answers, setAnswers] = useState({}); 
  const [checklistDocuments, setChecklistDocuments] = useState([]);

  // Recalcule la liste de documents à chaque changement de réponses ou d'étapes terminées
  useEffect(() => {
    const neededDocs = [];

    /*
    -----------------------------------
    Étape 1 : Situation familiale
    -----------------------------------
    */
    const stFam = answers["etape_situationFamiliale"] || {};
    if (completedSteps.includes("etape_situationFamiliale")) {
      // Certificat de mariage
      if (stFam.q_etat_mariage === "oui") {
        neededDocs.push(ALL_DOCUMENTS.CERTIFICAT_MARIAGE);
        // Vérif langue
        if (stFam.q_langue_mariage === "autre") {
          neededDocs.push(ALL_DOCUMENTS.TRADUCTION_CERTIFIEE_MARIAGE);
        }
      } else if (stFam.q_etat_mariage === "non") {
        // Indisponible -> toujours listé, l'utilisateur sait qu'il doit consulter un avocat
        neededDocs.push(ALL_DOCUMENTS.CERTIFICAT_MARIAGE);
      }

      // Enfants mineurs
      if (stFam.q_enfants_mineurs === "oui") {
        neededDocs.push(ALL_DOCUMENTS.CERTIFICAT_NAISSANCE_ENFANTS);
        if (stFam.q_langue_enfants === "autre") {
          neededDocs.push(ALL_DOCUMENTS.TRADUCTION_CERTIFIEE_ENFANTS);
        }
      }

      // Certificat de naissance époux
      if (stFam.q_certificat_naissance_epoux === "oui") {
        neededDocs.push(ALL_DOCUMENTS.CERTIFICAT_NAISSANCE_EPOUX);
        if (stFam.q_langue_epoux === "autre") {
          neededDocs.push(ALL_DOCUMENTS.TRADUCTION_CERTIFIEE_EPOUX);
        }
      } else if (stFam.q_certificat_naissance_epoux === "non") {
        neededDocs.push(ALL_DOCUMENTS.CERTIFICAT_NAISSANCE_EPOUX);
      }
    }

    /*
    -----------------------------------
    Étape 2 : Situation financière
    -----------------------------------
    */
    const stFin = answers["etape_finances"] || {};
    if (completedSteps.includes("etape_finances")) {
      // Déclarations d'impôts
      neededDocs.push(
        ALL_DOCUMENTS.DECLARATION_IMPOTS_FEDERALE,
        ALL_DOCUMENTS.DECLARATION_IMPOTS_PROVINCIALE,
        ALL_DOCUMENTS.AVIS_COTISATION
      );

      // Relevés de paie
      neededDocs.push(ALL_DOCUMENTS.RELEVES_PAIE);

      // Travail autonome ?
      if (stFin.q_entreprise === "oui") {
        neededDocs.push(ALL_DOCUMENTS.RELEVE_T4_ET_ETATS_FINANCIERS);
      }
    }

    /*
    -----------------------------------
    Étape 3 : Procédure de séparation de corps
    -----------------------------------
    */
    const stProc = answers["etape_procedureSeparation"] || {};
    if (completedSteps.includes("etape_procedureSeparation")) {
      // Demande introductive
      if (stProc.q_demande_separation === "oui") {
        neededDocs.push(ALL_DOCUMENTS.DEMANDE_INTRODUCTIVE_SEPARATION);
      } else {
        neededDocs.push(ALL_DOCUMENTS.FORMULAIRE_REPONSE);
      }

      // Mesures provisoires
      if (stProc.q_mesures_provisoires === "oui") {
        neededDocs.push(ALL_DOCUMENTS.DEMANDE_MESURES_PROVISOIRES);
      }

      // Ordonnance de sauvegarde
      if (stProc.q_ordonnance_sauvegarde === "oui") {
        neededDocs.push(ALL_DOCUMENTS.ORDONNANCE_SAUVEGARDE);
      }

      // Liste des pièces
      neededDocs.push(ALL_DOCUMENTS.LISTE_PIECES);

      // Protocole d'instance (on l'ajoute systématiquement, 
      // car possible contestation ou besoin d'organisation)
      neededDocs.push(ALL_DOCUMENTS.PROTOCOLE_INSTANCE);
    }

    /*
    -----------------------------------
    Étape 4 : Pension alimentaire
    -----------------------------------
    */
    const stPens = answers["etape_pensionAlimentaire"] || {};
    if (completedSteps.includes("etape_pensionAlimentaire")) {
      // Pension enfant
      if (stPens.q_pension_enfants === "oui") {
        neededDocs.push(ALL_DOCUMENTS.ANNEXE_I_PENSION_ENFANTS);
      }
      // Pension époux
      if (stPens.q_pension_epoux === "oui") {
        neededDocs.push(ALL_DOCUMENTS.FORMULAIRE_PENSION_EPOUX_FORM_III);
      }
      // Déclaration 444
      if (stPens.q_declaration_444 === "oui") {
        neededDocs.push(ALL_DOCUMENTS.FORMULAIRE_444);
      } else {
        // Si "non/pas sûr" mais pension = 444 quand même requise
        if (stPens.q_pension_enfants === "oui" || stPens.q_pension_epoux === "oui") {
          neededDocs.push(ALL_DOCUMENTS.FORMULAIRE_444);
        }
      }
    }

    /*
    -----------------------------------
    Étape 5 : Documents accessoires
    -----------------------------------
    */
    const stAccess = answers["etape_documentsAccessoires"] || {};
    if (completedSteps.includes("etape_documentsAccessoires")) {
      if (stAccess.q_contrat_mariage === "oui") {
        neededDocs.push(ALL_DOCUMENTS.CONTRAT_DE_MARIAGE);
      }
      if (stAccess.q_liste_actifs_passifs === "oui") {
        neededDocs.push(ALL_DOCUMENTS.LISTE_ACTIFS_PASSIFS);
      }
    }

    /*
    -----------------------------------
    Retrait des doublons
    -----------------------------------
    */
    const uniqueDocs = Array.from(new Set(neededDocs));
    setChecklistDocuments(uniqueDocs);
  }, [answers, completedSteps]);

  /*
  -------------------------------------------------------------------------
  Fonctions utilitaires
  -------------------------------------------------------------------------
  */
  const handleAnswerChange = (stepId, questionId, value) => {
    setAnswers((prev) => ({
      ...prev,
      [stepId]: { ...prev[stepId], [questionId]: value },
    }));
  };

  const areAllQuestionsAnswered = (step) => {
    const stepAns = answers[step.id] || {};
    if (!step.questions || step.questions.length === 0) return true;

    return step.questions.every((q) => {
      // Si showIf, on ne teste que si la question est visible
      if (q.showIf && !q.showIf(answers)) return true;
      const val = stepAns[q.id];
      if (!val || (typeof val === "string" && val.trim() === "")) {
        return false;
      }
      return true;
    });
  };

  const confirmStepCompletion = (step) => {
    return window.confirm(`Confirmez-vous avoir terminé l'étape "${step.title}" ?`);
  };

  const getVisibleSteps = () => stepsData;

  const markStepAsDone = (step) => {
    if (!areAllQuestionsAnswered(step)) {
      alert("Veuillez répondre à toutes les questions requises avant de terminer cette étape.");
      return;
    }
    // Vérifier que les étapes précédentes sont complétées
    const visible = getVisibleSteps();
    const currentIndex = visible.findIndex((s) => s.id === step.id);
    const previousSteps = visible.slice(0, currentIndex);
    for (const prev of previousSteps) {
      if (!completedSteps.includes(prev.id)) {
        alert(`Veuillez d'abord compléter l'étape "${prev.title}".`);
        return;
      }
    }
    // Confirmation
    if (!confirmStepCompletion(step)) return;

    if (!completedSteps.includes(step.id)) {
      setCompletedSteps((prev) => [...prev, step.id]);
    }
    setOpenStep(null);
  };

  const resetAll = () => {
    if (!window.confirm("Voulez-vous vraiment réinitialiser le questionnaire ?")) return;
    setCompletedSteps([]);
    setOpenStep(null);
    setAnswers({});
    setChecklistDocuments([]);
  };

  const progressPercent = Math.round((completedSteps.length / stepsData.length) * 100);
  const completedStepsCount = completedSteps.length;
  const totalSteps = stepsData.length;

  return (
    <div className="min-h-screen bg-gray-100">
      <main className="max-w-4xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="px-6 py-8 mt-4 bg-blue-50 rounded-xl border border-blue-100 shadow-sm mb-8">
          <h1 className="text-2xl font-bold text-blue-700 mb-3 text-center">
            <span className="inline-block h-1 bg-warning w-8 mr-2 rounded-full align-middle"></span>
            Checklist interactive de séparation
            <span className="inline-block h-1 bg-warning w-8 ml-2 rounded-full align-middle"></span>
          </h1>
          <p className="text-center text-blue-600 max-w-3xl mx-auto">
            Cette checklist interactive vous permet d'identifier tous les documents nécessaires 
            pour votre procédure de séparation selon votre situation personnelle.
          </p>
        </div>

        {/* Top 3 cards */}
        <div className="grid md:grid-cols-3 gap-6 mb-8">
          {/* Card 1: Progress */}
          <div className="card bg-white border border-slate-200 shadow-sm p-6 transition-all duration-200 hover:shadow-md">
            <h3 className="font-semibold text-blue-700 mb-3 flex items-center">
              <span className="bg-blue-50 p-2 rounded-full mr-2">📊</span>
              Progression
            </h3>
            <div className="mb-4">
              <div className="flex justify-between text-sm mb-1">
                <span className="font-semibold">{progressPercent}% complété</span>
                <span className="text-slate-500 text-xs">
                  {completedStepsCount} / {totalSteps} étapes
                </span>
              </div>
              <div className="w-full bg-slate-100 rounded-full h-3">
                <div
                  className="bg-blue-500 h-3 rounded-full transition-all duration-500 ease-out"
                  style={{ width: `${progressPercent}%` }}
                ></div>
              </div>
            </div>
          </div>

          {/* Card 2: Documents Count */}
          <div className="card bg-white border border-slate-200 shadow-sm p-6 transition-all duration-200 hover:shadow-md">
            <h3 className="font-semibold text-blue-700 mb-3 flex items-center">
              <span className="bg-blue-50 p-2 rounded-full mr-2">📄</span>
              Documents requis
            </h3>
            <div className="flex items-center justify-center h-16">
              {checklistDocuments.length === 0 ? (
                <p className="text-slate-500 text-center text-sm">
                  Complétez des étapes pour voir les documents requis
                </p>
              ) : (
                <div className="text-center">
                  <p className="text-2xl font-bold text-blue-700">
                    {checklistDocuments.length}
                  </p>
                  <p className="text-sm text-slate-600">documents à préparer</p>
                </div>
              )}
            </div>
          </div>

          {/* Card 3: Actions */}
          <div className="card bg-white border border-slate-200 shadow-sm p-6 transition-all duration-200 hover:shadow-md">
            <h3 className="font-semibold text-blue-700 mb-3 flex items-center">
              <span className="bg-blue-50 p-2 rounded-full mr-2">🔄</span>
              Actions
            </h3>
            <div className="flex justify-center items-center h-16">
              <button
                onClick={resetAll}
                className="btn btn-outline border-red-300 text-red-700 hover:bg-red-50 hover:border-red-400"
              >
                Réinitialiser tout
              </button>
            </div>
          </div>
        </div>

        {/* Documents Checklist */}
        {checklistDocuments.length > 0 && (
          <div className="mb-8">
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
              <h2 className="text-xl font-semibold text-blue-700 mb-6 flex items-center">
                <span className="bg-blue-50 p-2 rounded-full mr-2">📋</span>
                Liste des documents à préparer
              </h2>
              
              {/* Documents administratifs */}
              <div className="mb-8">
                <h3 className="font-semibold text-lg mb-4 text-blue-700 flex items-center">
                  <span className="bg-blue-50 p-1 rounded-full mr-2 text-sm">🏛️</span>
                  Documents administratifs
                </h3>
                <div className="grid gap-4 md:grid-cols-2">
                  {checklistDocuments
                    .filter(doc => doc.category === "Documents administratifs")
                    .map((doc) => (
                      <div 
                        key={doc.id}
                        className="bg-white rounded-lg border border-slate-200 p-4 hover:border-blue-200 hover:shadow-md transition-all duration-200"
                      >
                        <h4 className="font-medium text-blue-800 mb-2">{doc.label}</h4>
                        <p className="text-sm text-slate-600">{doc.description}</p>
                      </div>
                    ))}
                </div>
              </div>

              {/* Documents juridiques */}
              <div>
                <h3 className="font-semibold text-lg mb-4 text-blue-700 flex items-center">
                  <span className="bg-blue-50 p-1 rounded-full mr-2 text-sm">⚖️</span>
                  Documents juridiques
                </h3>
                <div className="grid gap-4 md:grid-cols-2">
                  {checklistDocuments
                    .filter(doc => doc.category === "Documents juridiques")
                    .map((doc) => (
                      <div 
                        key={doc.id}
                        className="bg-white rounded-lg border border-slate-200 p-4 hover:border-blue-200 hover:shadow-md transition-all duration-200"
                      >
                        <h4 className="font-medium text-blue-800 mb-2">{doc.label}</h4>
                        <p className="text-sm text-slate-600">{doc.description}</p>
                      </div>
                    ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Timeline steps */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="p-4 bg-blue-100 text-blue-700">
            <h2 className="text-lg font-bold flex items-center">
              <span className="mr-3 text-2xl">📝</span>
              Questionnaire interactif
            </h2>
            <p className="text-sm mt-1">
              Répondez aux questions pour générer votre liste de documents personnalisée.
            </p>
          </div>

          <div className="p-6">
            <div className="relative">
              <div className="absolute bg-slate-200 w-[3px] h-full left-4 md:left-6 top-0 z-0" />
              <div className="space-y-6">
                {stepsData.map((step, index) => {
                  const isDone = completedSteps.includes(step.id);
                  const isOpen = openStep === step.id;
                  const canOpen = stepsData
                    .slice(0, index)
                    .every((s) => completedSteps.includes(s.id));

                  return (
                    <div key={step.id} className="flex flex-col md:flex-row relative z-10">
                      <div className="mr-6">
                        <div
                          className={`w-8 h-8 rounded-full border-4 ${
                            isDone ? "border-green-500 bg-green-50" : "border-slate-400 bg-white"
                          } flex items-center justify-center text-sm font-bold transition-colors`}
                          style={{ marginLeft: "-2px" }}
                        >
                          {index + 1}
                        </div>
                      </div>
                      <div className="flex-1">
                        <div
                          className={`card border shadow-sm transition-all ${
                            isDone ? "bg-green-50 border-green-200" : "bg-white border-slate-200"
                          } hover:shadow-md`}
                        >
                          <div className="card-body p-6">
                            <div className="flex flex-col md:flex-row md:items-center md:justify-between">
                              <h2
                                className={`card-title text-base ${
                                  isDone ? "text-green-700" : "text-slate-700"
                                }`}
                              >
                                {step.title}
                              </h2>
                            </div>
                            <p className="mt-2 text-sm text-slate-600">{step.shortContent}</p>

                            <div className="mt-3 flex gap-3 flex-wrap">
                              <button
                                className={`btn btn-sm ${
                                  isOpen ? "btn-neutral" : "btn-primary"
                                } ${!canOpen ? "opacity-50 cursor-not-allowed" : ""}`}
                                onClick={() => canOpen && setOpenStep(isOpen ? null : step.id)}
                                disabled={!canOpen}
                              >
                                {isOpen ? "Masquer détails" : "Voir détails"}
                              </button>
                              {!isDone && (
                                <button
                                  className="btn btn-sm btn-success hover:bg-green-600 transition-colors"
                                  onClick={() => markStepAsDone(step)}
                                >
                                  Terminer l'étape
                                </button>
                              )}
                              {isDone && (
                                <span className="text-green-700 font-semibold self-center ml-2 text-sm flex items-center">
                                  <svg className="w-5 h-5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                                  </svg>
                                  Terminée
                                </span>
                              )}
                            </div>

                            {/* Questions panel */}
                            {isOpen && (
                              <div className="mt-4 p-4 border rounded-lg bg-slate-50 space-y-4">
                                <div
                                  className="text-sm text-slate-600"
                                  dangerouslySetInnerHTML={{ __html: step.detailedContent }}
                                />

                                {step.questions?.length > 0 && (
                                  <div className="space-y-6 mt-4">
                                    {step.questions.map((q) => {
                                      if (q.showIf && !q.showIf(answers)) return null;

                                      const stepAns = answers[step.id] || {};
                                      const selectedValue = stepAns[q.id] || "";

                                      if (q.options) {
                                        return (
                                          <div key={q.id} className="space-y-2">
                                            <label className="font-medium text-slate-700 block">
                                              {q.label}
                                            </label>
                                            {q.help && (
                                              <p className="text-xs text-slate-500">{q.help}</p>
                                            )}
                                            <div className="ml-4 space-y-2">
                                              {q.options.map((opt) => (
                                                <label
                                                  key={opt.value}
                                                  className="cursor-pointer flex items-center gap-2 hover:bg-slate-100 p-2 rounded-md transition-colors"
                                                >
                                                  <input
                                                    type="radio"
                                                    className="radio radio-primary"
                                                    name={`${step.id}-${q.id}`}
                                                    value={opt.value}
                                                    checked={selectedValue === opt.value}
                                                    onChange={() =>
                                                      handleAnswerChange(step.id, q.id, opt.value)
                                                    }
                                                  />
                                                  <span className="text-slate-700">{opt.label}</span>
                                                </label>
                                              ))}
                                            </div>
                                          </div>
                                        );
                                      }
                                      return null;
                                    })}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        {/* Legal note */}
        <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 text-sm text-slate-600 mt-8">
          <p className="italic text-center">
            Note : Cette checklist est fournie à titre informatif. Pour des conseils juridiques adaptés 
            à votre situation, consultez un professionnel du droit.
          </p>
        </div>
      </main>
    </div>
  );
}
